import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import { FiArrowRight, FiCode, FiRefreshCw, FiTool, FiShoppingCart, FiSearch, FiCpu } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import '../styles/Home.css';
import ModernCTA from '../components/ModernCTA.js';

// Import all images
import WebDevelopment from '../assets/Web Development.jpg';
import WebreDevelopment from '../assets/Web Re-Development.jpg';
import ecommerce from '../assets/E-Commerce Services.jpg';
import WebMaint from '../assets/Web Maintenance.jpg';
import seo from '../assets/Web Promotion.jpg';
import softwere from '../assets/Software Development.jpg';
import webservice from '../assets/Web Services.jpg';

const Home = () => {
  const [activeCard, setActiveCard] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Hero carousel items
  const carouselItems = [
    {
      id: 1,
      title: "Professional Web Development",
      description: "Custom websites built with modern technologies to elevate your online presence",
      imageUrl: WebDevelopment,
      cta: "Explore Services",
      link: "/services"
    },
    {
      id: 2,
      title: "E-Commerce Solutions",
      description: "Powerful online stores designed to maximize conversions and sales",
      imageUrl: ecommerce,
      cta: "Start Building",
      link: "/contact"
    },
    {
      id: 3,
      title: "Web Maintenance & Support",
      description: "Comprehensive care plans to keep your website running smoothly",
      imageUrl: WebMaint,
      cta: "Learn More",
      link: "/contact"
    }
  ];

  // Web Services Cards
  const serviceCards = [
    {
      id: 1,
      title: "Web Development",
      description: "Custom website development using the latest technologies tailored to your business needs",
      icon: <FiCode className="card-icon" />,
      imageUrl: WebDevelopment,
      link: "/services/web-development"
    },
    {
      id: 2,
      title: "Web Re-Development",
      description: "Modernize and improve existing websites with enhanced functionality and design",
      icon: <FiRefreshCw className="card-icon" />,
      imageUrl: WebreDevelopment,
      link: "/services/web-redevelopment"
    },
    {
      id: 3,
      title: "Web Maintenance",
      description: "Ongoing support, updates, and maintenance to keep your website secure and up-to-date",
      icon: <FiTool className="card-icon" />,
      imageUrl: WebMaint,
      link: "/services/web-maintenance"
    },
    {
      id: 4,
      title: "E-Commerce Services",
      description: "Complete online store solutions with payment integration and inventory management",
      icon: <FiShoppingCart className="card-icon" />,
      imageUrl: ecommerce,
      link: "/services/ecommerce-services"
    },
    {
      id: 5,
      title: "Web Promotion (SEO)",
      description: "Search engine optimization to increase your website's visibility and organic traffic",
      icon: <FiSearch className="card-icon" />,
      imageUrl: seo,
      link: "/services/web-promotion-seo"
    },
    {
      id: 6,
      title: "Software Development",
      description: "Custom software solutions designed to streamline your business operations",
      icon: <FiCpu className="card-icon" />,
      imageUrl: softwere,
      link: "/services/software-development"
    }
  ];

  // Features list
  const features = [
    {
      icon: <FiCode />,
      title: "Responsive Design",
      description: "Mobile-friendly websites that work perfectly on all devices",
    },
    {
      icon: <FiRefreshCw />,
      title: "Performance Optimized",
      description: "Fast-loading websites with optimized code and assets",
    },
    {
      icon: <FiTool />,
      title: "Security Focused",
      description: "Secure websites with regular updates and protection",
    },
    {
      icon: <FiShoppingCart />,
      title: "E-Commerce Ready",
      description: "Complete online store solutions with payment gateways",
    }
  ];

  // Testimonials  
  const testimonials = [
    {
      quote: "PCDS Infotech completely transformed our outdated website into a modern, high-performing platform that increased our leads by 150%",
      author: "Priya Sharma, Marketing Head - Mumbai"
    },
    {
      quote: "Their e-commerce solution helped us scale our online sales by 300% in just six months",
      author: "Sanjay Patel, Founder - Delhi NCR"
    },
    {
      quote: "The SEO services provided excellent results, moving us to page one for our key search terms",
      author: "Ananya Reddy, Small Business Owner - Bangalore"
    }
  ];

  // Animation variants
  const containerVariants = {
    acting: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  const fadeIn = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.8 }
    }
  };

  return (
    <div className="home-container">
      {/* Hero Carousel Section */}
      <section className="hero-section">
        <Carousel 
          showArrows={true} 
          infiniteLoop={true} 
          showThumbs={false} 
          showStatus={false} 
          autoPlay={true} 
          interval={6000}
          transitionTime={800}
          emulateTouch={true}
          swipeable={true}
          dynamicHeight={false}
          className="hero-carousel"
        >
          {carouselItems.map((item) => (
            <div key={item.id} className="carousel-slide">
              <div className="carousel-image-container">
                <img 
                  src={item.imageUrl} 
                  alt={item.title} 
                  className="carousel-image" 
                  loading="lazy"
                />
                <div className="carousel-overlay"></div>
              </div>
              <div className="carousel-content">
                <motion.h1 
                  className="carousel-title"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  {item.title}
                </motion.h1>
                <motion.p 
                  className="carousel-description"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  {item.description}
                </motion.p>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <Link to={item.link} className="carousel-cta">
                    {item.cta} <FiArrowRight className="cta-icon" />
                  </Link>
                </motion.div>
              </div>
            </div>
          ))}
        </Carousel>
      </section>

      {/* Services Section */}
      <section className="services-section">
        <div className="section-header">
          <motion.h2 
            initial="hidden"
            animate={isVisible ? "visible" : "hidden"}
            variants={fadeIn}
          >
            Our Web Services
          </motion.h2>
          <motion.p
            initial="hidden"
            animate={isVisible ? "visible" : "hidden"}
            variants={fadeIn}
            transition={{ delay: 0.2 }}
          >
            Comprehensive web solutions to grow your business online
          </motion.p>
        </div>

        <motion.div 
          initial="hidden"
          animate={isVisible ? "visible" : "hidden"}
          variants={containerVariants}
          className="cards-grid-container"
        >
          <div className="cards-grid">
            {serviceCards.map((card) => (
              <motion.div
                key={card.id}
                variants={cardVariants}
                whileHover={{ y: -10, scale: 1.02 }}
                className={`service-card ${activeCard === card.id ? 'active' : ''}`}
                onMouseEnter={() => setActiveCard(card.id)}
                onMouseLeave={() => setActiveCard(null)}
              >
                <div className="card-image-container">
                  <img 
                    src={card.imageUrl} 
                    alt={card.title} 
                    className="card-image"
                    loading="lazy"
                  />
                  <div className="card-overlay"></div>
                </div>
                <div className="card-content">
                  <div className="card-icon-container">
                    {card.icon}
                  </div>
                  <h3 className="card-title">{card.title}</h3>
                  <p className="card-description">{card.description}</p>
                  <Link to={card.link} className="card-cta">
                    Learn More <FiArrowRight className="cta-icon" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="section-header">
          <h2>Why Choose PCDS Infotech</h2>
          <p>Professional web solutions with measurable results</p>
        </div>
        <div className="features-grid">
          {features.map((feature, index) => (
            <motion.div 
              key={index}
              className="feature-card"
              initial="hidden"
              animate={isVisible ? "visible" : "hidden"}
              variants={cardVariants}
              transition={{ delay: index * 0.1 }}
            >
              <div className="feature-icon">{feature.icon}</div>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Additional Content Section */}
      <section className="info-section">
        <motion.div 
          className="info-content"
          initial="hidden"
          animate={isVisible ? "visible" : "hidden"}
          variants={fadeIn}
        >
          <h2>Transform Your Online Presence</h2>
          <p>
            PCDS Infotech provides comprehensive web services to help businesses establish and grow their online presence. 
            From initial website development to ongoing maintenance and promotion, we offer end-to-end solutions tailored 
            to your specific requirements.
          </p>
          <p>
            With expertise across all major web technologies and platforms, we deliver professional solutions that 
            integrate seamlessly with your business while meeting the highest standards of quality and performance.
          </p>
          <div className="stats-container">
            <div className="stat-item">
              <h3>200+</h3>
              <p>Websites successfully delivered</p>
            </div>
            <div className="stat-item">
              <h3>3-4 weeks</h3>
              <p>Average time to launch standard website</p>
            </div>
            <div className="stat-item">
              <h3>24/7</h3>
              <p>Support available for all clients</p>
            </div>
          </div>
        </motion.div>
        <motion.div 
          className="info-image"
          initial="hidden"
          animate={isVisible ? "visible" : "hidden"}
          variants={fadeIn}
          transition={{ delay: 0.3 }}
        >
          <img 
            src={webservice} 
            alt="Web Services"
            loading="lazy"
          />
        </motion.div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section">
        <div className="section-header">
          <h2>Client Success Stories</h2>
          <p>Trusted by businesses to deliver exceptional web solutions</p>
        </div>
        <div className="testimonials-container">
          {testimonials.map((testimonial, index) => (
            <motion.div 
              key={index}
              className="testimonial-card"
              initial="hidden"
              animate={isVisible ? "visible" : "hidden"}
              variants={cardVariants}
              transition={{ delay: index * 0.2 }}
            >
              <div className="testimonial-quote">
                "{testimonial.quote}"
              </div>
              <div className="testimonial-author">
                — {testimonial.author}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="services-cta-section">
        <ModernCTA />
      </section>
    </div>
  );
};

export default Home;